











































































































































































































































































































































import { computed, defineComponent, PropType, ref } from '@vue/composition-api';
import { createTabletRenderer, TabletRenderer } from '@oare/oare';
import {
  Word,
  EpigraphicUnit,
  EpigraphicWord,
  TextDiscourseRow,
  LocaleCode,
} from '@oare/types';
import sl from '@/serviceLocator';
import DictionaryWord from '@/views/DictionaryWord/index.vue';
import SealList from '@/views/Seals/SealList.vue';
import SingleSeal from '@/views/Seals/SingleSeal.vue';
import ConnectSpellingOccurrence from './ConnectSpellingOccurrence.vue';
import UtilList from '@/components/UtilList/index.vue';
import { formatLineNumber, romanNumeral } from '@oare/oare/src/tabletUtils';
import i18n from '@/i18n';
import utils from '@/utils';

export interface EpigraphySealLink {
  textEpigraphyUuid: string;
  linkedSealUuid: string | null;
}

export default defineComponent({
  name: 'EpigraphyReading',
  components: {
    DictionaryWord,
    ConnectSpellingOccurrence,
    SealList,
    SingleSeal,
    UtilList,
  },
  props: {
    textUuid: {
      type: String,
      required: false,
    },
    epigraphicUnits: {
      type: Array as PropType<EpigraphicUnit[]>,
      required: true,
    },
    epigraphyDiscourseToHighlight: {
      type: String,
      required: false,
    },
    localDiscourseInfo: {
      type: Array as PropType<TextDiscourseRow[]>,
      required: false,
    },
    commentMode: {
      type: Boolean,
      default: false,
    },
    disableEditing: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const store = sl.get('store');
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');
    const loading = ref(false);
    const viewingDialog = ref(false);
    const viewingConnectSpellingDialog = ref(false);
    const viewingConnectSealDialog = ref(false);
    const isCommenting = ref(false);
    const commentDialogUuid = ref('');
    const commentDialogItem = ref('');
    const commentDialogParent = ref('');
    const commentDiscourseUuid = ref('');
    const connectSpellingDialogSpelling = ref('');
    const connectSpellingDialogDiscourseUuid = ref('');
    const discourseWordInfo = ref<Word | null>(null);
    const sealLink = ref<EpigraphySealLink | null>(null);
    const interLinearView = ref(false);

    const canConnectSpellings = computed(() =>
      store.hasPermission('CONNECT_SPELLING')
    );

    const canDisconnectSpellings = computed(() =>
      store.hasPermission('DISCONNECT_OCCURRENCES')
    );

    const canConnectSealImpression = computed(() =>
      store.hasPermission('ADD_SEAL_LINK')
    );

    const selectedDiscourseUuid = ref('');

    const confirmDisconnectDialog = ref(false);

    const disconnectSpelling = async (): Promise<void> => {
      try {
        if (selectedDiscourseUuid.value) {
          await server.disconnectSpellings([selectedDiscourseUuid.value]);
          viewingDialog.value = false;
          actions.showSnackbar('Word successfully disconnected.');
        }
      } catch (err) {
        actions.showErrorSnackbar(
          'Error disconnecting word. Please try again.',
          err as Error
        );
      }
    };

    const renderer = ref<TabletRenderer>(
      createTabletRenderer(props.epigraphicUnits, i18n.locale as LocaleCode, {
        showNullDiscourse: store.getters.isAdmin,
        textFormat: 'html',
      })
    );

    const lineNumber = (line: number): string => {
      if (
        renderer.value.isRegion(line) ||
        renderer.value.isUndetermined(line)
      ) {
        return '';
      }

      const lineNumber = formatLineNumber(line);
      return lineNumber;
    };

    const openDialog = async (discourseUuid: string | null) => {
      try {
        loading.value = true;
        actions.showSnackbar('Fetching discourse information...');
        if (discourseUuid) {
          selectedDiscourseUuid.value = discourseUuid;
        }
        const spellingUuid = props.localDiscourseInfo
          ? props.localDiscourseInfo.filter(
              row => row.uuid === discourseUuid
            )[0].spellingUuid
          : null;

        if (discourseUuid && !props.localDiscourseInfo) {
          discourseWordInfo.value =
            await server.getDictionaryInfoByDiscourseUuid(discourseUuid);
        } else if (spellingUuid && props.localDiscourseInfo) {
          discourseWordInfo.value =
            await server.getDictionaryInfoBySpellingUuid(spellingUuid);
        } else {
          discourseWordInfo.value = null;
        }
        actions.closeSnackbar();
        if (discourseWordInfo.value) {
          viewingDialog.value = true;
        } else if (canConnectSpellings.value && discourseUuid) {
          await openConnectSpellingDialog(discourseUuid);
        } else {
          actions.showSnackbar(
            'No information exists for this text discourse word'
          );
        }
      } catch (err) {
        actions.showErrorSnackbar(
          'Failed to retrieve text discourse word info',
          err as Error
        );
      } finally {
        loading.value = false;
      }
    };

    const openConnectSpellingDialog = async (discourseUuid: string) => {
      try {
        const { spelling } = await server.getSpellingByDiscourseUuid(
          discourseUuid
        );
        viewingConnectSpellingDialog.value = true;
        connectSpellingDialogSpelling.value = spelling;
        connectSpellingDialogDiscourseUuid.value = discourseUuid;
      } catch (err) {
        actions.showErrorSnackbar(
          'Failed to load connect spelling view',
          err as Error
        );
      }
    };

    const closeConnectSpellingDialog = async () => {
      try {
        viewingConnectSpellingDialog.value = false;
        connectSpellingDialogSpelling.value = '';
        connectSpellingDialogDiscourseUuid.value = '';
      } catch (err) {
        actions.showErrorSnackbar(
          'Failed to close connect spelling view',
          err as Error
        );
      }
    };

    const openConnectSealImpressionDialog = async (lineNum: number) => {
      try {
        if (canConnectSealImpression.value) {
          const region: EpigraphicUnit | null =
            renderer.value.getRegionUnitByLine(lineNum);
          if (
            region &&
            region.uuid &&
            region.markups[0].type.includes('SealImpression')
          ) {
            sealLink.value = {
              textEpigraphyUuid: region.uuid,
              linkedSealUuid: await server.getLinkedSeal(region.uuid),
            };

            viewingConnectSealDialog.value = true;
          } else {
            actions.showSnackbar('No region information');
          }
        }
      } catch (err) {
        actions.showErrorSnackbar(
          'error retrieving region information',
          err as Error
        );
      }
    };

    const formatWord = (word: EpigraphicWord) => {
      const isWordToHighlight =
        props.epigraphyDiscourseToHighlight && word.discourseUuid
          ? props.epigraphyDiscourseToHighlight.includes(word.discourseUuid)
          : false;
      return isWordToHighlight ? `<mark>${word.reading}</mark>` : word.reading;
    };

    const formatSide = (side: EpigraphicUnit) => {
      if (side.markups.map(markup => markup.type).includes('uncertain')) {
        return `${side.side}<sup>?</sup>`;
      } else if (
        side.markups.map(markup => markup.type).includes('isEmendedReading')
      ) {
        return `${side.side}<sup>!</sup>`;
      }
      return side.side;
    };

    const formatTranslation = (translation: string) => {
      return translation.length > 25
        ? `&quot;${translation.slice(0, 22)}...&quot;`
        : `${
            translation !== 'PN' && translation !== 'GN' ? '&quot;' : ''
          }${translation}${
            translation !== 'PN' && translation !== 'GN' ? '&quot;' : ''
          }`;
    };

    const formatForm = (form: string) => {
      return `<em>${form}</em>`;
    };

    const formatDictionaryWord = (dictionaryWord: string) => {
      return `<em>${dictionaryWord}</em>`;
    };

    const canComment = computed(() => store.hasPermission('ADD_COMMENTS'));

    const openComment = (
      uuid: string,
      item: string,
      parent: string,
      discourseUuid: string
    ) => {
      commentDialogUuid.value = uuid;
      commentDialogItem.value = item;
      commentDialogParent.value = parent;
      commentDiscourseUuid.value = discourseUuid;
      isCommenting.value = true;
    };

    // To avoid circular dependencies
    const commentComponent = computed(() =>
      canComment.value
        ? () => import('@/components/CommentItemDisplay/index.vue')
        : null
    );

    return {
      renderer,
      lineNumber,
      openDialog,
      openConnectSealImpressionDialog,
      loading,
      discourseWordInfo,
      viewingDialog,
      viewingConnectSpellingDialog,
      viewingConnectSealDialog,
      connectSpellingDialogSpelling,
      connectSpellingDialogDiscourseUuid,
      closeConnectSpellingDialog,
      formatWord,
      formatSide,
      formatTranslation,
      formatDictionaryWord,
      formatForm,
      romanNumeral,
      formGrammarString: utils.formGrammarString,
      canDisconnectSpellings,
      canConnectSealImpression,
      confirmDisconnectDialog,
      disconnectSpelling,
      selectedDiscourseUuid,
      server,
      sealLink,
      interLinearView,
      commentDialogUuid,
      commentComponent,
      commentDialogItem,
      commentDialogParent,
      commentDiscourseUuid,
      isCommenting,
      canComment,
      openComment,
    };
  },
});
